import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Trophy, 
  TrendingUp, 
  Clock, 
  Target, 
  BarChart3, 
  RefreshCw,
  Award,
  CheckCircle,
  XCircle,
  Calendar,
  BookOpen
} from 'lucide-react';
import { apiService } from '../services/api';
import { toast } from 'sonner';

// CDN Logo URLs (DevIcon)
const LOGO_URLS = {
  RH124: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redhat/redhat-original.svg',
  RH134: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redhat/redhat-original.svg',
  SVRADM: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg',
  ANSIBLE: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ansible/ansible-original.svg',
  DOCKER: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg',
  TERRAFORM: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg',
  GIT: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg',
  PYTHON: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg',
  C_LANG: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg',
  CPP: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg',
  MYSQL: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg',
  JAVA: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg',
  KUBERNETES: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg',
  AWS_CCP: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg',
  AWS_SAA: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg',
};

// Category colors
const CATEGORY_COLORS = {
  RH124: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  RH134: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  SVRADM: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  ANSIBLE: { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' },
  DOCKER: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  TERRAFORM: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
  GIT: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  PYTHON: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  C_LANG: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  CPP: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  MYSQL: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  JAVA: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  KUBERNETES: { bg: 'bg-blue-50', text: 'text-indigo-700', border: 'border-blue-200' },
  AWS_CCP: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  AWS_SAA: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
};

const getCategoryFromTitle = (title) => {
  if (!title) return null;
  if (title.includes('RH124') || title.includes('Linux Fundamentals')) return 'RH124';
  if (title.includes('RH134') || title.includes('Linux Administration')) return 'RH134';
  if (title.includes('SVRADM') || title.includes('Server')) return 'SVRADM';
  if (title.includes('Ansible') || title.includes('ANSIBLE')) return 'ANSIBLE';
  if (title.includes('Docker') || title.includes('DOCKER')) return 'DOCKER';
  if (title.includes('Terraform') || title.includes('TERRAFORM')) return 'TERRAFORM';
  if (title.includes('Kubernetes') || title.includes('KUBERNETES')) return 'KUBERNETES';
  if (title.includes('Git') || title.includes('GIT')) return 'GIT';
  if (title.includes('Python') || title.includes('PYTHON')) return 'PYTHON';
  if (title.includes('C_LANG') || title.includes('C Programming')) return 'C_LANG';
  if (title.includes('CPP') || title.includes('C++')) return 'CPP';
  if (title.includes('MySQL') || title.includes('MYSQL') || title.includes('MariaDB')) return 'MYSQL';
  if (title.includes('Java') || title.includes('JAVA')) return 'JAVA';
  if (title.includes('AWS_CCP') || title.includes('Cloud Practitioner')) return 'AWS_CCP';
  if (title.includes('AWS_SAA') || title.includes('Solutions Architect')) return 'AWS_SAA';
  return null;
};

// Get category from test_id (for old data that doesn't have category)
const getCategoryFromTestId = (testId) => {
  if (testId >= 101 && testId <= 104) return 'RH124';
  if (testId >= 201 && testId <= 204) return 'RH134';
  if (testId >= 301 && testId <= 304) return 'SVRADM';
  if (testId >= 401 && testId <= 404) return 'ANSIBLE';
  if (testId >= 501 && testId <= 504) return 'DOCKER';
  if (testId >= 601 && testId <= 604) return 'TERRAFORM';
  if (testId >= 701 && testId <= 704) return 'GIT';
  if (testId >= 801 && testId <= 804) return 'PYTHON';
  if (testId >= 901 && testId <= 904) return 'C_LANG';
  if (testId >= 1001 && testId <= 1004) return 'CPP';
  if (testId >= 1101 && testId <= 1103) return 'MYSQL';
  if (testId >= 1201 && testId <= 1203) return 'JAVA';
  if (testId >= 1301 && testId <= 1303) return 'KUBERNETES';
  if (testId >= 1401 && testId <= 1404) return 'AWS_CCP';
  if (testId >= 1501 && testId <= 1503) return 'AWS_SAA';
  return null;
};

// Get test name from ID (for old data migration)
const TEST_NAMES = {
  101: 'RH124 Test 1: Linux Fundamentals & Command Line',
  102: 'RH124 Test 2: File Management & Permissions',
  103: 'RH124 Test 3: User & Group Management',
  104: 'RH124 Test 4: Process & Service Management',
  201: 'RH134 Test 1: Shell Scripting & Automation',
  202: 'RH134 Test 2: SELinux & Security',
  203: 'RH134 Test 3: Storage & LVM',
  204: 'RH134 Test 4: Network & Time Configuration',
  301: 'SVRADM Test 1: SSH & Remote Access',
  302: 'SVRADM Test 2: Web Server (Apache/Nginx)',
  303: 'SVRADM Test 3: DNS & NFS Services',
  304: 'SVRADM Test 4: Database (MariaDB/MySQL)',
  401: 'Ansible Test 1: Basics & Ad-hoc Commands',
  402: 'Ansible Test 2: Playbooks & YAML',
  403: 'Ansible Test 3: Roles & Vault',
  404: 'Ansible Test 4: Advanced Topics',
  501: 'Docker Test 1: Container Basics',
  502: 'Docker Test 2: Dockerfile & Building Images',
  503: 'Docker Test 3: Networking & Volumes',
  504: 'Docker Test 4: Docker Compose & Podman',
  601: 'Terraform Test 1: IaC Fundamentals',
  602: 'Terraform Test 2: Variables & Outputs',
  603: 'Terraform Test 3: State Management',
  604: 'Terraform Test 4: Modules & Advanced',
  701: 'Git Test 1: Fundamentals',
  702: 'Git Test 2: Branching & Merging',
  703: 'Git Test 3: Advanced Operations',
  704: 'Git Test 4: Collaboration & Workflows',
  // Programming Courses
  801: 'Python Test 1: Basics & Syntax',
  802: 'Python Test 2: Functions & OOP',
  803: 'Python Test 3: File I/O & Data Structures',
  901: 'C Test 1: Basics & Pointers',
  902: 'C Test 2: Arrays & Strings',
  903: 'C Test 3: Memory Management',
  1001: 'C++ Test 1: OOP Concepts',
  1002: 'C++ Test 2: Inheritance & Polymorphism',
  1003: 'C++ Test 3: STL & Templates',
  1101: 'MySQL Test 1: SQL Basics & DDL',
  1102: 'MySQL Test 2: Joins & Subqueries',
  1103: 'MySQL Test 3: Indexes & Administration',
  1201: 'Java Test 1: Basics & OOP',
  1202: 'Java Test 2: Collections & Generics',
  1203: 'Java Test 3: Multithreading & JVM',
  1301: 'Kubernetes Test 1: Core Concepts & Pods',
  1302: 'Kubernetes Test 2: Services & Networking',
  1303: 'Kubernetes Test 3: Helm & Advanced',
  1401: 'AWS CCP Test 1: Cloud Fundamentals & IAM',
  1402: 'AWS CCP Test 2: EC2, S3 & Storage',
  1403: 'AWS CCP Test 3: Databases, ELB & Auto Scaling',
  1404: 'AWS CCP Test 4: Networking, Billing & Well-Architected',
  1501: 'AWS SAA Test 1: VPC, Networking & Security',
  1502: 'AWS SAA Test 2: Databases, Serverless & HA',
  1503: 'AWS SAA Test 3: Cost Optimization & Exam Scenarios',
};

const MockTestDashboard = ({ onSelectTest }) => {
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [leaderboardCategory, setLeaderboardCategory] = useState('ALL');

  const CATEGORIES = ['ALL', 'RH124', 'RH134', 'SVRADM', 'ANSIBLE', 'DOCKER', 'TERRAFORM', 'KUBERNETES', 'GIT', 'PYTHON', 'C_LANG', 'CPP', 'MYSQL', 'JAVA', 'AWS_CCP', 'AWS_SAA'];

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    loadLeaderboard();
  }, [leaderboardCategory]);

  const loadDashboardData = async () => {
    try {
      const [statsResponse, leaderboardResponse] = await Promise.all([
        apiService.getMockTestStats(),
        apiService.getMockTestLeaderboard(5, leaderboardCategory)
      ]);

      setStats(statsResponse);
      setLeaderboard(leaderboardResponse.leaderboard || []);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderboard = async () => {
    try {
      const response = await apiService.getMockTestLeaderboard(5, leaderboardCategory);
      setLeaderboard(response.leaderboard || []);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    }
  };

  const handleResetProgress = async () => {
    if (!window.confirm('Are you sure you want to reset all your mock test progress? This action cannot be undone.')) {
      return;
    }

    setResetting(true);
    try {
      await apiService.resetMockTestProgress();
      toast.success('Mock test progress has been reset successfully');
      await loadDashboardData();
    } catch (error) {
      console.error('Failed to reset progress:', error);
      toast.error('Failed to reset progress');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your progress...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium">Total Attempts</p>
                <p className="text-3xl font-bold text-blue-900">{stats?.total_attempts || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <Target className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium">Tests Passed</p>
                <p className="text-3xl font-bold text-green-900">{stats?.tests_passed || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 text-sm font-medium">Average Score</p>
                <p className="text-3xl font-bold text-purple-900">{stats?.average_score || 0}%</p>
              </div>
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-600 text-sm font-medium">Best Score</p>
                <p className="text-3xl font-bold text-yellow-900">{stats?.best_score || 0}%</p>
              </div>
              <div className="w-12 h-12 bg-yellow-600 rounded-lg flex items-center justify-center">
                <Trophy className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Attempts & Leaderboard */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Attempts */}
        <Card className="shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center text-lg">
              <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
              Recent Attempts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_attempts?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_attempts.slice(0, 5).map((attempt, index) => {
                  // Get category - try from attempt, then from title, then from test_id
                  const category = attempt.category || 
                                   getCategoryFromTitle(attempt.test_title) || 
                                   getCategoryFromTestId(attempt.test_id);
                  
                  // Get proper test title - use actual title or fallback to mapped name
                  const displayTitle = (attempt.test_title && !attempt.test_title.startsWith('Mock Test ')) 
                    ? attempt.test_title 
                    : (TEST_NAMES[attempt.test_id] || attempt.test_title);
                  
                  const logo = category ? LOGO_URLS[category] : null;
                  const colors = (category && CATEGORY_COLORS[category]) ? CATEGORY_COLORS[category] : { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' };
                  
                  return (
                    <div key={attempt.id} className={`flex items-center gap-4 p-4 rounded-xl border ${colors.border} ${colors.bg} transition-all hover:shadow-md`}>
                      {/* Logo */}
                      <div className="flex-shrink-0">
                        {logo ? (
                          <img src={logo} alt={category} className="w-10 h-10 rounded-lg" />
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                            <BookOpen className="h-5 w-5 text-gray-500" />
                          </div>
                        )}
                      </div>
                      
                      {/* Test Info */}
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold ${colors.text} text-sm truncate`}>
                          {displayTitle}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(attempt.completed_at).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {Math.floor(attempt.time_spent / 60)} min
                          </span>
                          {category && (
                            <Badge variant="outline" className="text-xs px-1.5 py-0">
                              {category}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* Score */}
                      <div className="flex-shrink-0 text-right">
                        <div className={`text-lg font-bold ${attempt.passed ? 'text-green-600' : 'text-red-600'}`}>
                          {attempt.score_percentage}%
                        </div>
                        <div className="flex items-center justify-end gap-1 mt-0.5">
                          {attempt.passed ? (
                            <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                          ) : (
                            <XCircle className="h-3.5 w-3.5 text-red-500" />
                          )}
                          <span className={`text-xs font-medium ${attempt.passed ? 'text-green-600' : 'text-red-600'}`}>
                            {attempt.passed ? 'Passed' : 'Failed'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Clock className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No attempts yet</p>
                <p className="text-sm">Start your first mock test!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leaderboard */}
        <Card className="shadow-lg">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center text-lg">
                <Trophy className="h-5 w-5 mr-2 text-yellow-600" />
                Top Performers
              </CardTitle>
              {/* Category Filter */}
              <select
                value={leaderboardCategory}
                onChange={(e) => setLeaderboardCategory(e.target.value)}
                className="text-sm border rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === 'ALL' ? 'All Courses' : cat}
                  </option>
                ))}
              </select>
            </div>
          </CardHeader>
          <CardContent>
            {leaderboard.length > 0 ? (
              <div className="space-y-3">
                {leaderboard.map((user, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-yellow-500 text-white' :
                        index === 1 ? 'bg-gray-400 text-white' :
                        index === 2 ? 'bg-orange-500 text-white' :
                        'bg-gray-200 text-gray-700'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{user.user_name}</p>
                        <p className="text-xs text-gray-600">{user.total_attempts} attempts</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">{user.best_score}%</p>
                      <p className="text-xs text-gray-500">best score</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Award className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No leaderboard data</p>
                <p className="text-sm">Be the first to complete a test!</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button 
          onClick={() => onSelectTest(null)} 
          className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
        >
          Start New Test
        </Button>
        <Button 
          onClick={handleResetProgress} 
          variant="outline"
          disabled={resetting || !stats?.total_attempts}
          className="px-8 py-3"
        >
          {resetting ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Resetting...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset Progress
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default MockTestDashboard;